/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box, Grid, Typography, Paper, Button, Chip, Stack, Avatar, IconButton, Container, Divider,
} from "@mui/material";
import React, { useEffect, useContext } from "react";
import { Header } from "../../components/Header";
import {
  AddressCard,
  PasswordCard,
  ProfileCard,
} from "../../components/Profile";
import Analytics from "../../utils/analytics";
import UserContext from "../../context/User";
import StarIcon from "@mui/icons-material/Star";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import VerifiedIcon from "@mui/icons-material/Verified";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { gql, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { myLoyaltyPoints, myLoyaltyCoupons } from "../../apollo/server";

const MY_LOYALTY = gql`${myLoyaltyPoints}`;
const MY_COUPONS = gql`${myLoyaltyCoupons}`;

const TIER_STYLES = {
  SILVER:   { bg: "rgba(255,255,255,0.18)", color: "#fff", border: "rgba(255,255,255,0.35)" },
  GOLD:     { bg: "rgba(252, 211, 77, 0.22)", color: "#fde68a", border: "rgba(252, 211, 77, 0.6)" },
  PLATINUM: { bg: "rgba(196, 181, 253, 0.22)", color: "#ddd6fe", border: "rgba(196, 181, 253, 0.65)" },
};

function StatCard({ icon, label, value, sub }) {
  return (
    <Paper elevation={0} sx={{
      p: 2.5, borderRadius: "16px", border: "1px solid #e5e7eb",
      display: "flex", alignItems: "center", gap: 1.75, height: "100%",
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: "12px",
        backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "#6b7280", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#111827", lineHeight: 1.25 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: "0.72rem" }}>{sub}</Typography>
        )}
      </Box>
    </Paper>
  );
}

function QuickAction({ to, icon, label, accent }) {
  return (
    <Button component={Link} to={to} disableElevation
      sx={{
        textTransform: "none", flexDirection: "column", gap: 0.75, py: 2,
        borderRadius: "14px", border: "1px solid #e5e7eb", backgroundColor: "#fff",
        width: "100%", minHeight: 90,
        "&:hover": { backgroundColor: "#f9fafb", borderColor: accent || "#FF3008" },
      }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: "10px",
        backgroundColor: (accent || "#FF3008") + "14",
        color: accent || "#FF3008",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#111827" }}>{label}</Typography>
    </Button>
  );
}

function Profile() {
  const { profile } = useContext(UserContext);
  const { data: loyaltyData } = useQuery(MY_LOYALTY, { fetchPolicy: "cache-and-network" });
  const { data: couponsData } = useQuery(MY_COUPONS, { fetchPolicy: "cache-and-network" });
  const loyalty = loyaltyData?.myLoyaltyPoints;
  const activeCouponsCount = (couponsData?.myLoyaltyCoupons || []).filter(c => c.status === "ACTIVE" || c.status === "ISSUED").length;
  const tierStyle = loyalty ? (TIER_STYLES[loyalty.tier] || TIER_STYLES.SILVER) : TIER_STYLES.SILVER;

  useEffect(async () => {
    await Analytics.track(Analytics.events.NAVIGATE_TO_PROFILE);
  }, []);

  const initial = profile?.name ? profile.name[0].toUpperCase() : "?";
  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString([], { month: "long", year: "numeric" })
    : null;

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      {/* Hero */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)",
        pt: { xs: 5, md: 7 }, pb: { xs: 12, md: 14 },
        position: "relative", overflow: "hidden",
        "&::before": { content: '""', position: "absolute", top: -120, right: -120, width: 380, height: 380, borderRadius: "50%", background: "rgba(255,255,255,0.04)" },
        "&::after":  { content: '""', position: "absolute", bottom: -160, left: -100, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.03)" },
      }}>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "center", sm: "flex-end" }} gap={3} sx={{ textAlign: { xs: "center", sm: "left" } }}>
            <Avatar sx={{
              width: { xs: 96, md: 112 }, height: { xs: 96, md: 112 },
              fontSize: { xs: "2.2rem", md: "2.6rem" }, fontWeight: 800,
              backgroundColor: "#fff", color: "#FF3008",
              border: "4px solid rgba(255,255,255,0.25)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            }}>{initial}</Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent={{ xs: "center", sm: "flex-start" }}>
                <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.02em" }}>
                  {profile?.name || "Welcome"}
                </Typography>
                {profile?.emailIsVerified && (
                  <Chip
                    size="small"
                    icon={<VerifiedIcon sx={{ fontSize: 14, color: "#86efac !important" }} />}
                    label="Verified"
                    sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, backgroundColor: "rgba(34,197,94,0.18)", color: "#bbf7d0", border: "1px solid rgba(34,197,94,0.35)" }}
                  />
                )}
              </Stack>
              <Stack direction="row" gap={2} flexWrap="wrap" justifyContent={{ xs: "center", sm: "flex-start" }} sx={{ mt: 0.75 }}>
                {profile?.email && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <MailOutlineIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.6)" }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.85rem" }}>{profile.email}</Typography>
                  </Stack>
                )}
                {profile?.phone && (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <PhoneIphoneIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.6)" }} />
                    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "0.85rem" }}>{profile.phone}</Typography>
                  </Stack>
                )}
              </Stack>
              {memberSince && (
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", mt: 0.75 }}>
                  Member since {memberSince}
                </Typography>
              )}
            </Box>
            {/* Tier card on right */}
            {loyalty && (
              <Paper elevation={0} sx={{
                px: 2.5, py: 2, borderRadius: "16px",
                backgroundColor: tierStyle.bg, border: `1px solid ${tierStyle.border}`,
                backdropFilter: "blur(8px)", minWidth: 180,
              }}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <StarIcon sx={{ color: tierStyle.color, fontSize: 24 }} />
                  <Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.68rem", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                      {loyalty.tier} Tier
                    </Typography>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.25rem", lineHeight: 1.1 }}>
                      {loyalty.balance.toLocaleString()}<Box component="span" sx={{ fontSize: "0.85rem", ml: 0.5, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>pts</Box>
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Pulled-up content panel */}
      <Container maxWidth="lg" sx={{ mt: -10, mb: 4, position: "relative", zIndex: 2 }}>
        {/* Stats row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<StarIcon sx={{ color: "#f59e0b" }} />}
              label="Points"
              value={loyalty ? loyalty.balance.toLocaleString() : "0"}
              sub="Available to spend"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<CardGiftcardIcon sx={{ color: "#7c3aed" }} />}
              label="Coupons"
              value={activeCouponsCount}
              sub={activeCouponsCount === 1 ? "Active coupon" : "Active coupons"}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<ReceiptLongIcon sx={{ color: "#FF3008" }} />}
              label="Lifetime"
              value={loyalty ? loyalty.lifetimePoints.toLocaleString() : "0"}
              sub="Points earned"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<LocalShippingIcon sx={{ color: "#16a34a" }} />}
              label="Tier"
              value={loyalty?.tier || "SILVER"}
              sub={loyalty?.nextTier ? `${loyalty.pointsToNextTier} pts to ${loyalty.nextTier}` : "Top tier"}
            />
          </Grid>
        </Grid>

        {/* Quick actions */}
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", mb: 1.5 }}>Quick actions</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/rewards" icon={<StarIcon />} label="Rewards" accent="#f59e0b" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/my-coupons" icon={<CardGiftcardIcon />} label="My Coupons" accent="#7c3aed" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/orders" icon={<ReceiptLongIcon />} label="My Orders" accent="#FF3008" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/favourite" icon={<FavoriteIcon />} label="Favourites" accent="#ec4899" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/wallet" icon={<AccountBalanceWalletIcon />} label="Wallet" accent="#0ea5e9" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <QuickAction to="/get-help" icon={<HeadsetMicIcon />} label="Get Help" accent="#475569" />
            </Grid>
          </Grid>
        </Paper>

        {/* Two-column: account + addresses */}
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 2.5, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "10px", backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <MailOutlineIcon sx={{ color: "#FF3008", fontSize: 18 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Account Information</Typography>
              </Stack>
              <ProfileCard />
            </Paper>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "10px", backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <VerifiedIcon sx={{ color: "#1e40af", fontSize: 18 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Security</Typography>
              </Stack>
              <PasswordCard />
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: "16px", border: "1px solid #e5e7eb" }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: "10px", backgroundColor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LocalShippingIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>Saved Addresses</Typography>
              </Stack>
              <AddressCard />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Profile;
